import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import OneSignal from "react-onesignal";
import { getUserId } from "@/utils/token_functions";
import { updateProfileAsync } from "@/store/redux/slices/user/user_slice";

const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID;

const useOneSignal = () => {
  const dispatch = useDispatch();
  const [initialized, setInitialized] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const initStartedRef = useRef(false);
  const lastSyncedPlayerIdRef = useRef(undefined);

  const syncPlayerIdToProfile = useCallback(
    async (playerId) => {
      if (lastSyncedPlayerIdRef.current === playerId) return;
      lastSyncedPlayerIdRef.current = playerId;

      try {
        await dispatch(updateProfileAsync({ playerId })).unwrap();
      } catch (err) {
        
        console.error("useOneSignal: failed to sync playerId:", err);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!ONESIGNAL_APP_ID) {
      console.warn(
        "useOneSignal: VITE_ONESIGNAL_APP_ID is not set. Push notifications are disabled."
      );
      return;
    }

    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const init = async () => {
      try {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
         
          promptOptions: {
            slidedown: { enabled: false },
          },
          notifyButton: { enable: false },
          allowLocalhostAsSecureOrigin: true,
        });

        const userId = getUserId();
        if (userId) {
          await OneSignal.login(userId);
        }

        const alreadySubscribed = OneSignal.User.PushSubscription.optedIn;
        const currentPlayerId = OneSignal.User.PushSubscription.id || null;
        setIsSubscribed(!!alreadySubscribed);
        setInitialized(true);
        await syncPlayerIdToProfile(currentPlayerId);
      } catch (err) {
        console.error("useOneSignal: init failed:", err);
        initStartedRef.current = false;
      }
    };

    init();
  }, [syncPlayerIdToProfile]);

  const optIn = async () => {
    if (!initialized) return;
    try {
      await OneSignal.User.PushSubscription.optIn();
      const subscribed = !!OneSignal.User.PushSubscription.optedIn;
      const playerId = OneSignal.User.PushSubscription.id || null;
      setIsSubscribed(subscribed);
      await syncPlayerIdToProfile(playerId);
    } catch (err) {
      console.error("useOneSignal: optIn failed:", err);
    }
  };

  const optOut = async () => {
    if (!initialized) return;
    try {
      await OneSignal.User.PushSubscription.optOut();
      setIsSubscribed(false);
      await syncPlayerIdToProfile(null);
    } catch (err) {
      console.error("useOneSignal: optOut failed:", err);
    }
  };

  const promptPush = async () => {
    if (!initialized) return;
    try {
      await OneSignal.Slidedown.promptPush();
    } catch (err) {
      console.error("useOneSignal: promptPush failed:", err);
    }
  };

  return { initialized, isSubscribed, optIn, optOut, promptPush };
};

export default useOneSignal;
